import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isNumber } from '@iotv/datamodel';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { Alert, Box, Button, FormLabel, InputAdornment, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { AppMainViewContext } from '../../../../ContextProviders/AppContextProvider';
import { CoverageDAO } from '../../../../data/NetworkSystem/NetworkCoverage/requestCoverage';
import { useModularTimePeriodSelector } from '../../../../hooks/useModularTimePeriodSelector';
import { AutosizedTextField } from '../../../common/AutosizedTextField';
import { StripedDataGrid } from '../../../factories/DataGrid';
import { ProcessReceptionAreaRequestStatus } from '../../CloudSideTypes';
import { getValidation, getValidationMessage, isValidContext } from '../../Contexts';
import { NetworkCoverageContext } from '../../NetworkCoverageContext';
const debug = process.env.REACT_APP_DEBUG && false;
const CoverageKPIGridColumns = [
    { field: 'espKpi', headerName: 'ESP KPI', width: 75, disableColumnMenu: true },
    { field: 'espGood', headerName: 'ESP Good', width: 85, disableColumnMenu: true, valueFormatter: (v) => `${v} %` },
    { field: 'espOk', headerName: 'ESP OK', width: 70, disableColumnMenu: true, valueFormatter: (v) => `${v} %` },
    { field: 'espPoor', headerName: 'ESP Poor', width: 80, disableColumnMenu: true, valueFormatter: (v) => `${v} %` },
    {
        field: 'droppedULPercentKpiGood',
        headerName: 'UL KPI Good',
        width: 105,
        disableColumnMenu: true,
        valueFormatter: (v) => (v !== undefined ? `${v} %` : ''),
    },
    { field: 'droppedULPercentKpiOk', headerName: 'UL KPI OK', width: 90, disableColumnMenu: true, valueFormatter: (v) => (v !== undefined ? `${v} %` : '') },
    {
        field: 'droppedULPercentKpiPoor',
        headerName: 'UL KPI Poor',
        width: 100,
        disableColumnMenu: true,
        valueFormatter: (v) => (v !== undefined ? `${v} %` : ''),
    },
    { field: 'ULPerDay', headerName: 'UL per Day', width: 95, disableColumnMenu: true },
    { field: 'DLPerDay', headerName: 'DL per Day', width: 95, disableColumnMenu: true },
];
export const ParameterSelector = ({ hidePopup }) => {
    // main app state
    const [{ isSmallView, contextUser, contextCustomer }] = useContext(AppMainViewContext);
    // coverage reports state
    const { state: coverageState, dispatch } = useContext(NetworkCoverageContext);
    const { Properties, Areas, Places, Gateways, Networks, parameters, bounds } = coverageState;
    const fromDateMs = parameters.timePeriod?.fromDateMs;
    const toDateMs = parameters.timePeriod?.toDateMs;
    // coverage report generation / requests state
    //const [requestStatusMap, dispatchRequestStatusMapUpdate] = useContext(RequestStatusReducerContext)
    const dispatchRequestStatusMapUpdates = (context, requestId, userId) => {
        const { Areas, Places } = context;
        [...Areas, ...Places].forEach((vob) => {
            const preNotification = {
                GeoObject: vob,
                requestId,
                status: ProcessReceptionAreaRequestStatus.REQUESTED,
                userId,
            };
            debug && console.log(`ParameterSelector`, `sending prenotif for ${vob.name}`, ProcessReceptionAreaRequestStatus[preNotification.status]);
            dispatch({ type: 'addCoverageResult', result: preNotification });
        });
    };
    const [valid, setValid] = useState(false);
    const initialToTime = dayjs(toDateMs);
    const rangeLength = initialToTime.diff(dayjs(fromDateMs), 'hour');
    const { components: time, fromDateValue, toDateValue, fromDateError, toDateError, } = useModularTimePeriodSelector({ label: 'Select coverage period', rangeUnit: 'hours', rangeLength, initialToTime });
    /** Date range useEffect */
    useEffect(() => {
        if (fromDateValue && toDateValue) {
            const fromDateMs = fromDateValue.unix() * Milliseconds.SECOND;
            const toDateMs = toDateValue.unix() * Milliseconds.SECOND;
            const clone = { ...coverageState, parameters: { ...parameters, timePeriod: { fromDateMs, toDateMs } } };
            debug && console.log(`ParameterSelector used effect on to from dates`, clone);
            const action = { type: 'setParameters', params: clone.parameters };
            dispatch(action);
            const validationMap = getValidation(clone);
            setValid(isValidContext(validationMap));
        }
    }, [fromDateValue, toDateValue]);
    const setDistanceFromPointMeters = (distanceFromPointMeters) => {
        if (isNumber(distanceFromPointMeters)) {
            const clone = { ...coverageState, parameters: { ...parameters, distanceFromPointMeters } };
            debug && console.log(`ParameterSelector used effect on distanceFromPointMeters`, clone);
            const action = { type: 'setParameters', params: clone.parameters };
            dispatch(action);
            const validationMap = getValidation(clone);
            setValid(isValidContext(validationMap));
        }
    };
    const [sfFilterValues, setSfFilterValues] = useState([]);
    const handleRequestCoverage = async () => {
        debug && console.log('handleRequestCoverage Context ', coverageState);
        const { parameters: { timePeriod, distanceFromPointMeters }, } = coverageState;
        const extractedRequestState = {
            parameters: coverageState.parameters,
            Areas: coverageState.Areas,
            bounds: coverageState.bounds,
            Places: coverageState.Places,
            Gateways: coverageState.Gateways,
            Networks: coverageState.Networks,
            Properties: coverageState.Properties,
        };
        if (contextUser && timePeriod && distanceFromPointMeters) {
            const requestId = v4();
            const userId = contextUser.id;
            dispatchRequestStatusMapUpdates(extractedRequestState, requestId, userId);
            const requestParams = {
                ...extractedRequestState,
                parameters: { timePeriod, distanceFromPointMeters, readingFilter: { sf: sfFilterValues.length > 0 ? sfFilterValues : undefined } },
                requestId,
                userId: contextUser.id,
                customerId: contextCustomer?.id,
            };
            /** Mutate request params to pass Networks: [] and Gateways: [] so that report has all of them available */
            requestParams.Networks = [];
            requestParams.Gateways = [];
            debug &&
                console.log('handleRequestCoverage Context ', requestParams, {
                    from: new Date(requestParams.parameters.timePeriod.fromDateMs).toString(),
                    to: new Date(requestParams.parameters.timePeriod.toDateMs).toString(),
                });
            const { err, data } = await CoverageDAO.requestCoverageReport(requestParams); // This pushes to the user MQTT topic, subscribed to in NetworkCoverageWizard
            if (err || !data)
                console.error('handleRequestCoverage', { err, data });
        }
        else {
            debug && console.log('handleRequestCoverage no user ', contextUser);
        }
        hidePopup();
    };
    const validationMap = getValidation(coverageState);
    let validationMessage = getValidationMessage(validationMap);
    if (fromDateError)
        validationMessage += fromDateError;
    if (toDateError)
        validationMessage += toDateError;
    return (_jsxs(Stack, { alignItems: 'flex-start', gap: 1, children: [_jsx(Box, { sx: { border: (t) => `1px solid ${t.palette.action.disabled}`, borderRadius: 1, padding: 1 }, children: _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Period" }), _jsxs(Stack, { direction: 'row', gap: 1, children: [time.fromPicker, time.toPicker, time.periodField, time.rangeUnitRadio, time.resetToNowBtn] })] }) }, 'period'), _jsx(Box, { sx: { border: (t) => `1px solid ${t.palette.action.disabled}`, borderRadius: 1, padding: 1 }, children: _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Filters" }), _jsxs(Stack, { direction: "row", gap: 1, alignItems: "center", children: [_jsx(Typography, { children: "Spreading Factor" }), _jsxs(ToggleButtonGroup, { color: "primary", value: sfFilterValues, onChange: (e, o) => setSfFilterValues(o), sx: { [`.MuiToggleButtonGroup-grouped`]: { minWidth: '3em', paddingBlock: 1 } }, children: [_jsx(ToggleButton, { value: 7, children: "7" }), _jsx(ToggleButton, { value: 8, children: "8" }), _jsx(ToggleButton, { value: 9, children: "9" }), _jsx(ToggleButton, { value: 10, children: "10" }), _jsx(ToggleButton, { value: 11, children: "11" }), _jsx(ToggleButton, { value: 12, children: "12" })] }), (sfFilterValues.length === 0 || sfFilterValues.length === 6) && (_jsx(Alert, { sx: { paddingBlock: 0, alignItems: 'center' }, severity: "info", children: "All spreading factors" }))] })] }) }, 'places'), _jsx(Box, { sx: { border: (t) => `1px solid ${t.palette.action.disabled}`, borderRadius: 1, padding: 1, width: 'stretch' }, children: _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Places" }), _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: isSmallView ? 'flex-start' : 'center', gap: 1, children: [_jsx(Typography, { children: "Set distance from Places" }), _jsx(AutosizedTextField, { type: 'number',
                                    label: 'Distance',
                                    value: parameters.distanceFromPointMeters,
                                    slotProps: { input: { sx: { width: '7em' }, endAdornment: _jsx(InputAdornment, { position: "end", children: "m" }) } },
                                    onChange: (e) => setDistanceFromPointMeters(Math.abs(parseFloat(e.target.value))) }), _jsx(Alert, { severity: "info", sx: { paddingBlock: 0, alignItems: 'center' }, children: "The maximum distance from a Place where received data is associated with it" })] }, "distance"), _jsx(StripedDataGrid, { getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'), density: "compact", rowHeight: 30, hideFooter: true, disableRowSelectionOnClick: true, columns: [{ field: 'name', headerName: 'Name', minWidth: 100, flex: 1, disableColumnMenu: true }, ...CoverageKPIGridColumns], rows: Places })] }) }, "places"), _jsx(Box, { sx: { border: (t) => `1px solid ${t.palette.action.disabled}`, borderRadius: 1, padding: 1, width: 'stretch' }, children: _jsxs(Stack, { gap: 1, children: [_jsx(FormLabel, { children: "Areas" }), _jsx(StripedDataGrid, { getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'), density: "compact", rowHeight: 30, hideFooter: true, disableRowSelectionOnClick: true, columns: [{ field: 'name', headerName: 'Name', minWidth: 100, flex: 1, disableColumnMenu: true }, ...CoverageKPIGridColumns], rows: Areas })] }) }, "areas"), validationMessage.replace(/\s/g, '') != '' && _jsx(Alert, { severity: "error", children: validationMessage }), _jsx(Stack, { direction: "row", width: "100%", justifyContent: "flex-end", children: _jsx(Button, { variant: 'contained', color: 'success', onClick: handleRequestCoverage, disabled: !valid || !!(fromDateError || toDateError), children: "Request Coverage" }) }, "request")] }));
};
